import { Formik, FormikErrors } from 'formik';
import React from 'react';
import smoothScrollIntoView from 'smooth-scroll-into-view-if-needed';
import { NodeManager, useOrderedNodes } from '../utils/formHelpers';
import GlobalFormError from './GlobalFormError';
import { IFormProps } from './types';

const isPromise = (value: any) => {
  return (
    value !== null &&
    typeof value === 'object' &&
    typeof value.then === 'function'
  );
};

function FormHelper<FormValues extends {}>(props: IFormProps<FormValues>) {
  const hasErrors = (errors: FormikErrors<FormValues>) => {
    return Object.keys(errors).some((key) => {
      const value = (errors as Record<string, unknown>)[key];
      if (value !== '') {
        return true;
      }
      return false;
    });
  };

  const { inline = false } = props;

  const ordered = useOrderedNodes();
  const [shouldCheckForScroll, setShouldCheckForScroll] = React.useState(false);

  React.useEffect(() => {
    // only scroll to first error if there is no global error
    if (
      !props.globalErrorMessage &&
      shouldCheckForScroll &&
      ordered.length > 0
    ) {
      smoothScrollIntoView(ordered[0], {
        block: 'center',
        inline: 'center',
        scrollMode: 'if-needed'
      }).then(() => {
        let focusOn: HTMLElement | null = null;
        if (ordered[0].querySelector('input')) {
          focusOn = ordered[0].querySelector('input');
        } else {
          const focusId = ordered[0].id.substring(
            0,
            ordered[0].id.indexOf('-wrapper')
          );
          focusOn = focusId ? ordered[0].querySelector(`#${focusId}`) : null;
        }
        focusOn && focusOn.focus();
        setShouldCheckForScroll(false);
      });
    }
  }, [shouldCheckForScroll, ordered]);

  let resolveWait: any;
  let waitToResolve: Promise<boolean> = new Promise((resolve, reject) => {
    resolveWait = resolve;
  });
  const extraProps: Partial<IFormProps<FormValues>> = {
    onSubmit: (values: any, givenProps: any) => {
      props.onSubmit(values, givenProps);
      resolveWait(true);
    }
  };
  if (props.validate) {
    extraProps.validate = (values: any) => {
      const validated = (props.validate as any)(values);
      if (isPromise(validated)) {
        return validated.catch((error: any) => {
          resolveWait(false);
          throw error;
        });
      } else {
        if (
          validated &&
          typeof validated === 'object' &&
          Object.keys(validated).length
        ) {
          resolveWait(false);
        }
        return validated;
      }
    };
  }
  return (
    <Formik<FormValues>
      {...props}
      {...extraProps}
      render={renderProps => {
        const newProps = {
          ...renderProps,
          asyncSubmitForm: async () => {
            waitToResolve = new Promise((resolve, reject) => {
              resolveWait = resolve;
            });
            renderProps.submitForm();
            return waitToResolve;
          }
        };
        return (
          <form
            className={inline ? 'form-inline' : ''}
            onSubmit={e => {
              !props.globalErrorMessage && setShouldCheckForScroll(true);
              // resolves issues with multiple
              // Formiks on a single page
              e.stopPropagation();
              // actually handles submission
              renderProps.handleSubmit(e);
            }}
            noValidate={true}
            qa-id={props['qa-id']}
          >
            {props.globalErrorMessage &&
              renderProps.submitCount > 0 &&
              hasErrors(renderProps.errors) && (
                <GlobalFormError
                  submitCount={renderProps.submitCount}
                  globalErrorMessage={props.globalErrorMessage}
                />
              )}
            {props.render && props.render(newProps)}
            {props.children &&
              typeof props.children === 'function' &&
              props.children(newProps)}
          </form>
        );
      }}
    />
  );
}

export function Form<FormValues extends {}>(props: IFormProps<FormValues>) {
  return (
    <NodeManager>
      <FormHelper {...props} />
    </NodeManager>
  );
}
